/**
 * Main Application Component
 * Handles rider query flow: rider selection -> query type -> hill selection -> results
 * Saves last selected rider in a cookie for convenience
 * Also handles event results routing
 */

import { useState, useEffect } from 'react';
import { Navigate, Routes, Route, useLocation } from 'react-router-dom';
import RiderSelect from './components/RiderSelect';
import QueryTypeSelect from './components/QueryTypeSelect';
import HillSelect from './components/HillSelect';
import ResultsDisplay from './components/ResultsDisplay';
import PerformanceChart from './components/PerformanceChart';
import EventResults from './components/EventResults';
import YearSelect from './components/YearSelect';
import TSIResults from './components/TSIResults';
import EventDateSelect from './components/EventDateSelect';
import BellCurveChart from './components/BellCurveChart';
import './styles.css';
import SecretaryPortal from './components/SecretaryPortal';
import EntryListPublic from './components/EntryListPublic';
import Calendar from './components/Calendar';
import CurrentRecords from './components/CurrentRecords';
import CalendarEditor from './components/CalendarEditor';
import ResultsAdmin from './components/ResultsAdmin';
import NextEvent from './components/NextEvent';
import OpenChampionship from './components/OpenChampionship';
import ResultsHub from './components/ResultsHub';
import SiteHome from './components/site/SiteHome';
import { isWebsiteHost, PORTAL_PATHS, portalUrl } from './components/site/siteLinks';
import SitePhotos from './components/site/SitePhotos';
import SiteVideos from './components/site/SiteVideos';
import AppBar from './components/site/AppBar';
import DevBanner from './components/DevBanner';
import TargetTimes from './components/TargetTimes';
import SiteContact from './components/site/SiteContact';
import SiteAdmin from './components/site/SiteAdmin';
import SitePage from './components/site/SitePage';
import OnlineEntryForm from './components/OnlineEntryForm';
import EntryProofPage from './components/EntryProofPage';
import EntryProofBundlePage from './components/EntryProofBundlePage';
import ScoreRanges from './components/ScoreRanges';

function HomePage() {
  // Application state
  const [selectedRider, setSelectedRider] = useState(null);
  const [comparisonEnabled, setComparisonEnabled] = useState(false);
  const [comparisonRiders, setComparisonRiders] = useState([null, null]); // Up to 2 frenemies
  const [queryType, setQueryType] = useState(null);
  const [selectedHill, setSelectedHill] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [nextEvent, setNextEvent] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedEventDate, setSelectedEventDate] = useState(null);
  const [selectedRunTypes, setSelectedRunTypes] = useState(new Set(['Practice', 'Timed', 'Topten']));

  const RUN_TYPE_OPTIONS = ['Practice', 'Timed', 'Topten', 'Open'];

  const toggleRunType = (type) => {
    setSelectedRunTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  /**
   * Cookie utilities
   */
  const setCookie = (name, value, days = 365) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${encodeURIComponent(JSON.stringify(value))};expires=${expires.toUTCString()};path=/`;
  };

  const getCookie = (name) => {
    const nameEQ = name + "=";
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      let c = cookie.trim();
      if (c.indexOf(nameEQ) === 0) {
        try {
          return JSON.parse(decodeURIComponent(c.substring(nameEQ.length)));
        } catch {
          return null;
        }
      }
    }
    return null;
  };

  /**
   * Fetch the next upcoming event from calendar
   */
  const fetchNextEvent = async () => {
    try {
      const response = await fetch('/api/next-event', {
        headers: {
          'X-API-Key': import.meta.env.VITE_API_KEY || ''
        }
      });

      if (response.ok) {
        const data = await response.json();
        setNextEvent(data);
      }
    } catch (err) {
      // Silently fail - not critical
      console.error('Failed to fetch next event:', err);
    }
  };

  /**
   * Load saved rider and comparison settings on mount
   * Also fetch next upcoming event from calendar
   */
  useEffect(() => {
    const savedRider = getCookie('lastSelectedRider');
    const savedComparisonEnabled = getCookie('comparisonEnabled');
    const savedComparisonRiders = getCookie('comparisonRiders');

    if (savedRider) {
      setSelectedRider(savedRider);
    }
    if (savedComparisonEnabled !== null) {
      setComparisonEnabled(savedComparisonEnabled);
    }
    if (savedComparisonRiders) {
      setComparisonRiders(savedComparisonRiders);
    }

    // Fetch next upcoming event
    fetchNextEvent();
  }, []);

  /**
   * Handle rider selection
   * Stores rider in session state and cookie, advances to query type selection
   */
  const handleRiderSelect = (rider) => {
    setSelectedRider(rider);
    setQueryType(null);
    setSelectedHill(null);
    setSelectedEventDate(null);
    setResults(null);
    setError(null);
    
    // Save to cookie for next visit
    if (rider) {
      setCookie('lastSelectedRider', rider);
    }
  };

  /**
   * Handle comparison mode toggle
   */
  const handleComparisonToggle = (enabled) => {
    setComparisonEnabled(enabled);
    setQueryType(null);
    setSelectedHill(null);
    setSelectedEventDate(null);
    setResults(null);
    setError(null);
    
    // Clear comparison riders if disabling
    if (!enabled) {
      setComparisonRiders([null, null]);
    }
    
    setCookie('comparisonEnabled', enabled);
  };

  /**
   * Handle comparison rider selection
   */
  const handleComparisonRiderSelect = (index, rider) => {
    const newComparisonRiders = [...comparisonRiders];
    newComparisonRiders[index] = rider;
    setComparisonRiders(newComparisonRiders);
    setQueryType(null);
    setSelectedHill(null);
    setSelectedEventDate(null);
    setResults(null);
    setError(null);
    
    setCookie('comparisonRiders', newComparisonRiders);
  };

  /**
   * Handle query type selection
   * Currently only "Personal Best" but structured for future expansion
   */
  const handleQueryTypeSelect = (type) => {
    setQueryType(type);
    setSelectedHill(null);
    setSelectedYear(null);
    setSelectedEventDate(null);
    setResults(null);
    setError(null);
  };

  /**
   * Dedupe a list of rider objects by firstname+lastname (case-insensitive).
   * Prevents the same rider (e.g. restored from a cookie) being sent twice when
   * they're selected as both the primary rider and a comparison rider.
   */
  const dedupeRiders = (ridersList) => {
    const seen = new Set();
    return ridersList.filter(rider => {
      const key = `${rider.firstname.toLowerCase()}|${(rider.lastname || '').toLowerCase()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  /**
   * Build the run_types query string for Personal Best requests
   * run_types_filter=1 tells the backend the (possibly empty) list is authoritative,
   * rather than falling back to its own default
   */
  const runTypesQueryString = () => {
    const params = new URLSearchParams();
    selectedRunTypes.forEach(type => params.append('run_types', type));
    params.append('run_types_filter', '1');
    return params.toString();
  };

  /**
   * Handle hill selection and execute query
   * Routes to appropriate API endpoint based on query type
   * Fetches data for all riders if in comparison mode
   */
  const handleHillSelect = async (hill) => {
    setSelectedHill(hill);
    setResults(null);
    setError(null);
    setLoading(true);

    try {
      // In comparison mode for Personal Best, fetch all riders
      if (comparisonEnabled && queryType === 'Personal Best') {
        const ridersToFetch = dedupeRiders([
          selectedRider,
          ...comparisonRiders.filter(r => r !== null)
        ]);
        
        // Fetch data for all riders in parallel
        const promises = ridersToFetch.map(rider =>
          fetch(
            `/api/personal-best?firstname=${encodeURIComponent(rider.firstname)}&lastname=${encodeURIComponent(rider.lastname || '')}&hill=${encodeURIComponent(hill)}&${runTypesQueryString()}`,
            {
              headers: {
                'X-API-Key': import.meta.env.VITE_API_KEY || ''
              }
            }
          ).then(async response => {
            if (response.ok) {
              const data = await response.json();
              return { rider, data, error: null };
            } else if (response.status === 404) {
              return { rider, data: null, error: 'No results found' };
            } else {
              return { rider, data: null, error: 'Failed to fetch' };
            }
          }).catch(err => ({ rider, data: null, error: err.message }))
        );
        
        const allResults = await Promise.all(promises);
        setResults(allResults);
      } else {
        // Single rider mode
        let endpoint = '';
        
        // Determine which API endpoint to call based on query type
        if (queryType === 'Personal Best') {
          endpoint = `/api/personal-best?firstname=${encodeURIComponent(selectedRider.firstname)}&lastname=${encodeURIComponent(selectedRider.lastname || '')}&hill=${encodeURIComponent(hill)}&${runTypesQueryString()}`;
        } else if (queryType === 'Performance History') {
          endpoint = `/api/performance-history?firstname=${encodeURIComponent(selectedRider.firstname)}&lastname=${encodeURIComponent(selectedRider.lastname || '')}&hill=${encodeURIComponent(hill)}`;
        }
        
        const response = await fetch(endpoint, {
          headers: {
            'X-API-Key': import.meta.env.VITE_API_KEY || ''
          }
        });

        if (!response.ok) {
          if (response.status === 404) {
            const data = await response.json();
            throw new Error(data.message || 'No results found');
          }
          throw new Error('Failed to fetch results');
        }

        const data = await response.json();
        setResults(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Re-run the Personal Best query when the run-type filter changes
   * after a hill has already been selected
   */
  useEffect(() => {
    if (selectedHill && queryType === 'Personal Best') {
      handleHillSelect(selectedHill);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRunTypes]);

  /**
   * Handle hill selection for "Where in the Bell" — this query type needs
   * a specific event (hill + date), so picking a hill just advances to the
   * event-date step rather than firing a query straight away.
   */
  const handleBellHillSelect = (hill) => {
    setSelectedHill(hill);
    setSelectedEventDate(null);
    setResults(null);
    setError(null);
  };

  /**
   * Handle event date selection for "Where in the Bell" — fetches the full
   * field of results for that single event.
   */
  const handleBellEventSelect = async (date) => {
    setSelectedEventDate(date);
    setResults(null);
    setError(null);
    setLoading(true);

    try {
      const response = await fetch(
        `/api/event-results?hill=${encodeURIComponent(selectedHill)}&date=${date}`,
        { headers: { 'X-API-Key': import.meta.env.VITE_API_KEY || '' } }
      );

      if (!response.ok) {
        if (response.status === 404) {
          const data = await response.json();
          throw new Error(data.message || 'No results found for this event');
        }
        throw new Error('Failed to fetch event results');
      }

      const data = await response.json();
      setResults(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle year selection for TSI
   */
  const handleYearSelect = async (year) => {
    setSelectedYear(year);
    setResults(null);
    setError(null);
    setLoading(true);

    try {
      // Build riders array for TSI
      const ridersToFetch = dedupeRiders([
        selectedRider,
        ...comparisonRiders.filter(r => r !== null)
      ]);
      
      const ridersParam = encodeURIComponent(JSON.stringify(ridersToFetch));
      
      const response = await fetch(
        `/api/tsi?riders=${ridersParam}&year=${year}`,
        {
          headers: {
            'X-API-Key': import.meta.env.VITE_API_KEY || ''
          }
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          const data = await response.json();
          setResults({ results: [], common_events: [], message: data.message });
          return;
        }
        throw new Error('Failed to fetch TSI results');
      }

      const data = await response.json();
      setResults(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Reset the application to start a new query
   */
  const handleReset = () => {
    setSelectedRider(null);
    setQueryType(null);
    setSelectedHill(null);
    setSelectedEventDate(null);
    setResults(null);
    setError(null);
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>🏍️ NHCA Rider Stats</h1>
        <p>Analyze motorcycle hillclimb performance data</p>
      </header>

      {/* Next Event Banner */}
      {nextEvent && !bannerDismissed && (
        <div className="next-event-banner">
          <div className="banner-content">
            <span className="banner-icon">📅</span>
            <span className="banner-text">
              {nextEvent.daysUntil === 0 ? (
                <>Today&apos;s event at <strong>{nextEvent.hill}</strong></>
              ) : (
                <><strong>{nextEvent.daysUntil} {nextEvent.daysUntil === 1 ? 'day' : 'days'}</strong> until the next event at <strong>{nextEvent.displayHill}</strong></>
              )}
              {nextEvent.date && ` on ${new Date(nextEvent.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`}
              {nextEvent.closingDate && nextEvent.daysUntilClosing !== null && (
                <>
                  {nextEvent.daysUntilClosing > 0 && (
                    <>, <strong>entry closes in {nextEvent.daysUntilClosing} {nextEvent.daysUntilClosing === 1 ? 'day' : 'days'}</strong></>
                  )}
                  {nextEvent.daysUntilClosing === 0 && (
                    <>, <strong className="closing-today">entry closes today!</strong></>
                  )}
                  {nextEvent.daysUntilClosing < 0 && (
                    <>, <span className="entry-closed">entry closed {Math.abs(nextEvent.daysUntilClosing)} {Math.abs(nextEvent.daysUntilClosing) === 1 ? 'day' : 'days'} ago</span></>
                  )}
                </>
              )}
            </span>
            <button 
              className="banner-dismiss"
              onClick={() => setBannerDismissed(true)}
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
          {nextEvent.otherClosingSoon && nextEvent.otherClosingSoon.length > 0 && (
            <div className="banner-other-closing">
              <span className="banner-other-label">Also closing soon: </span>
              {nextEvent.otherClosingSoon.map((e, i) => (
                <span key={i} className="banner-other-item">
                  <strong>{e.hill}</strong> in {e.daysUntilClosing} {e.daysUntilClosing === 1 ? 'day' : 'days'}
                  {i < nextEvent.otherClosingSoon.length - 1 ? ', ' : ''}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <main className="App-main">
        {/* Step 1: Rider Selection */}
        <div className="query-step">
          <RiderSelect
            selectedRider={selectedRider}
            onSelect={handleRiderSelect}
            disabled={false}
          />
        </div>

        {/* Step 1.5: Comparison Mode Toggle */}
        {selectedRider && (
          <div className="query-step comparison-toggle">
            <label className="comparison-checkbox">
              <input
                type="checkbox"
                checked={comparisonEnabled}
                onChange={(e) => handleComparisonToggle(e.target.checked)}
              />
              <span>Add frenemies to compare</span>
            </label>
          </div>
        )}

        {/* Step 1.6: Comparison Rider Selection */}
        {selectedRider && comparisonEnabled && (
          <div className="query-step comparison-riders">
            <h3>🏁 Select Frenemies to Compare</h3>
            <p className="step-description">
              Add up to 2 riders to compare against {selectedRider.fullname}
            </p>
            
            <div className="comparison-rider-selects">
              <div className="comparison-rider-item">
                <label>Frenemy 1:</label>
                <RiderSelect
                  selectedRider={comparisonRiders[0]}
                  onSelect={(rider) => handleComparisonRiderSelect(0, rider)}
                  disabled={false}
                  excludeRiders={[selectedRider, comparisonRiders[1]]}
                />
              </div>

              <div className="comparison-rider-item">
                <label>Frenemy 2 (optional):</label>
                <RiderSelect
                  selectedRider={comparisonRiders[1]}
                  onSelect={(rider) => handleComparisonRiderSelect(1, rider)}
                  disabled={false}
                  excludeRiders={[selectedRider, comparisonRiders[0]]}
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Query Type Selection */}
        {selectedRider && (
          <div className="query-step">
            <QueryTypeSelect
              selectedType={queryType}
              onSelect={handleQueryTypeSelect}
              comparisonMode={comparisonEnabled}
              comparisonRiders={comparisonRiders}
            />
          </div>
        )}

        {/* Step 2.5: Run Type Filter (Personal Best only) */}
        {selectedRider && queryType === 'Personal Best' && (
          <div className="query-step">
            <h3>🏁 Filter by Run Type</h3>
            <p className="step-description">
              Choose which run types count towards the personal best
            </p>
            <div className="checkbox-group">
              {RUN_TYPE_OPTIONS.map(type => (
                <label key={type} className="check-pill">
                  <input
                    type="checkbox"
                    checked={selectedRunTypes.has(type)}
                    onChange={() => toggleRunType(type)}
                  />
                  {selectedRunTypes.has(type) ? '✓ ' : ''}{type}
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Hill or Year Selection */}
        {selectedRider && queryType && (
          <div className="query-step">
            {queryType === 'Tom Short Index' ? (
              <YearSelect
                selectedYear={selectedYear}
                onSelect={handleYearSelect}
                disabled={loading}
              />
            ) : (
              <HillSelect
                selectedHill={selectedHill}
                onSelect={queryType === 'Where in the Bell' ? handleBellHillSelect : handleHillSelect}
                disabled={loading}
              />
            )}
          </div>
        )}

        {/* Step 3.5: Event Date Selection (Where in the Bell only) */}
        {selectedRider && queryType === 'Where in the Bell' && selectedHill && (
          <div className="query-step">
            <EventDateSelect
              hill={selectedHill}
              selectedDate={selectedEventDate}
              onSelect={handleBellEventSelect}
              disabled={loading}
            />
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="loading">
            <div className="spinner"></div>
            <p>Fetching results...</p>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="error-message">
            <h3>⚠️ Error</h3>
            <p>{error}</p>
            <button onClick={() => setError(null)}>Dismiss</button>
          </div>
        )}

        {/* Results Display */}
        {results && !loading && (
          <div className="query-step">
            {comparisonEnabled && queryType === 'Personal Best' ? (
              <div className="comparison-results">
                <h2>🏁 Performance Comparison</h2>
                <p className="comparison-subtitle">at {selectedHill}</p>
                <div className="comparison-grid">
                  {results.map((result, index) => (
                    <div key={index} className="comparison-card">
                      {result.error ? (
                        <div className="comparison-error">
                          <h3>{result.rider.fullname}</h3>
                          <p className="error-text">❌ {result.error}</p>
                        </div>
                      ) : (
                        <ResultsDisplay
                          results={result.data}
                          rider={result.rider}
                          queryType={queryType}
                          hill={selectedHill}
                          compactMode={true}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : queryType === 'Performance History' ? (
              <PerformanceChart
                data={results}
                rider={selectedRider}
                hill={selectedHill}
              />
            ) : queryType === 'Tom Short Index' ? (
              <TSIResults
                results={results}
                year={selectedYear}
              />
            ) : queryType === 'Where in the Bell' ? (
              <BellCurveChart
                results={results}
                rider={selectedRider}
                comparisonRiders={comparisonEnabled ? comparisonRiders.filter(r => r !== null) : []}
                hill={selectedHill}
                date={selectedEventDate}
              />
            ) : (
              <ResultsDisplay
                results={results}
                rider={selectedRider}
                queryType={queryType}
                hill={selectedHill}
              />
            )}
            <button className="reset-button" onClick={handleReset}>
              Start New Query
            </button>
          </div>
        )}

        {/* Current Selection Summary */}
        {(selectedRider || queryType || selectedHill) && !results && (
          <div className="selection-summary">
            <h3>Current Selection:</h3>
            {selectedRider && (
              <p>
                <strong>Rider:</strong> {selectedRider.fullname}
                <button
                  className="change-button"
                  onClick={() => handleRiderSelect(null)}
                >
                  Change
                </button>
              </p>
            )}
            {queryType && (
              <p>
                <strong>Query:</strong> {queryType}
              </p>
            )}
          </div>
        )}
      </main>

      <footer className="App-footer">
        <p>Hillclimb Query App v1.0 | Data updated in real-time</p>
      </footer>
    </div>
  );
}

// The app's own pages get the slim NHCA bar with a link home. Everything
// else is the public website (home, photos, contact, Web Admin and the
// content pages), which has the full site header instead.
const APP_PATHS = ['/results', '/secretary', '/entries/', '/enter/', '/entry-proof/', '/entry-proof-bundle', '/calendar', '/records',
  '/target-times', '/calendar-editor', '/results-admin', '/next-event', '/championship', '/score-ranges', '/results-hub'];

function isAppPage(pathname) {
  return (pathname === '/' && !isWebsiteHost()) || APP_PATHS.some(p => (p.endsWith('/') ? pathname.startsWith(p) : pathname === p));
}

// A login page opened on www (an old bookmark, a typed address): go to the
// same page on query, where the logins live (siteLinks.js).
function ToQuery() {
  const { pathname, search } = useLocation();
  useEffect(() => { window.location.replace(portalUrl(pathname) + search); }, [pathname, search]);
  return null;
}

function App() {
  const { pathname } = useLocation();
  if (isWebsiteHost() && PORTAL_PATHS.includes(pathname)) return <ToQuery />;
  return (
    <>
    <DevBanner />
    {isAppPage(pathname) && <AppBar />}
    <Routes>
      <Route path="/" element={isWebsiteHost() ? <SiteHome /> : <HomePage />} />
      <Route path="/results" element={<EventResults />} />
      <Route path="/secretary"  element={<SecretaryPortal />} />
      <Route path="/entries/:calendarId" element={<EntryListPublic />} />
      <Route path="/enter/:calendarId" element={<OnlineEntryForm />} />
      <Route path="/entry-proof/:submissionId" element={<EntryProofPage />} />
      <Route path="/entry-proof-bundle" element={<EntryProofBundlePage />} />
      <Route path="/calendar" element={<Calendar />} />
      <Route path="/records" element={<CurrentRecords />} />
      <Route path="/target-times" element={<TargetTimes />} />
      <Route path="/calendar-editor" element={<CalendarEditor />} />
      <Route path="/results-admin" element={<ResultsAdmin />} />
      <Route path="/next-event" element={<NextEvent />} />
      <Route path="/championship" element={<OpenChampionship />} />
      <Route path="/score-ranges" element={<ScoreRanges />} />
      <Route path="/results-hub" element={<ResultsHub />} />
      {/* The website's home page away from www (on www it's "/") */}
      <Route path="/newsite" element={isWebsiteHost() ? <Navigate to="/" replace /> : <SiteHome />} />
      <Route path="/photos" element={<SitePhotos />} />
      <Route path="/videos" element={<SiteVideos />} />
      <Route path="/contact" element={<SiteContact />} />
      <Route path="/site-admin" element={<SiteAdmin />} />
      {/* Content pages at their old Wix addresses (/faq, /hills-prescott ...); unknown ones say "not found". */}
      <Route path="/:slug" element={<SitePage />} />
    </Routes>
    </>
  );
}

export default App;
